import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadHistory = ({ history }) => {
  const [expandedId, setExpandedId] = useState(null);

  const getStatusBadge = (status) => {
    const badges = {
      success: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: 'CheckCircle',
        label: 'Success'
      },
      partial: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: 'AlertTriangle',
        label: 'Partial Success'
      },
      failed: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: 'XCircle',
        label: 'Failed'
      }
    };

    const badge = badges?.[status] || badges?.failed;

    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${badge?.bg} ${badge?.text}`}>
        <Icon name={badge?.icon} size={14} />
        <span className="text-xs font-semibold">{badge?.label}</span>
      </div>
    );
  };

  const handleDownloadReport = (entry) => {
    alert(`Downloading detailed report for ${entry?.fileName}...\n\nReport includes:\n- Upload summary\n- Successful records\n- Failed records with reasons\n- Validation errors`);
  };

  const handleRetry = (entry) => {
    alert(`Retrying upload for ${entry?.fileName}...\n\nYou can fix the errors and re-upload the corrected file.`);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Icon name="History" size={24} className="text-[#ff8c00]" />
          Upload History
        </h2>
        <Button variant="outline" size="sm">
          <Icon name="Filter" size={16} />
          <span>Filter</span>
        </Button>
      </div>

      {history?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="FileX" size={64} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600">No upload history available</p>
        </div>
      ) : (
        <div className="space-y-3">
          {history?.map((entry) => (
            <div
              key={entry?.id}
              className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Header Row */}
              <div className="p-4 bg-gray-50 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="p-2 bg-white rounded-lg border border-gray-200">
                    <Icon
                      name={entry?.type === 'Sugar Marketplace' ? 'Package' : 
                            entry?.type === 'Vehicle Registration' ? 'Truck' : 'Users'}
                      size={20}
                      className="text-[#ff8c00]"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{entry?.fileName}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        {entry?.uploadDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={12} />
                        {entry?.uploadedBy}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(entry?.status)}
                  <button
                    onClick={() => setExpandedId(expandedId === entry?.id ? null : entry?.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Icon
                      name={expandedId === entry?.id ? 'ChevronUp' : 'ChevronDown'}
                      size={20}
                      className="text-gray-600"
                    />
                  </button>
                </div>
              </div>

              {/* Stats Bar */}
              <div className="px-4 py-3 bg-white border-t border-gray-200">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">Total Records</p>
                    <p className="text-lg font-bold text-gray-900">{entry?.totalRecords}</p>
                  </div>
                  <div className="text-center border-l border-r border-gray-200">
                    <p className="text-xs text-gray-600 mb-1">Successful</p>
                    <p className="text-lg font-bold text-green-600">{entry?.successfulRecords}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">Failed</p>
                    <p className="text-lg font-bold text-red-600">{entry?.failedRecords}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all"
                    style={{
                      width: `${((entry?.successfulRecords / entry?.totalRecords) * 100)?.toFixed(1)}%`
                    }}
                  />
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === entry?.id && (
                <div className="p-4 bg-gray-50 border-t border-gray-200 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-1">Upload Type</p>
                      <p className="text-sm text-gray-900">{entry?.type}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-1">Success Rate</p>
                      <p className="text-sm text-gray-900">
                        {((entry?.successfulRecords / entry?.totalRecords) * 100)?.toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  {entry?.errorMessage && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-red-900 mb-1">Error Details</p>
                      <p className="text-sm text-red-700">{entry?.errorMessage}</p>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadReport(entry)}
                    >
                      <Icon name="Download" size={16} />
                      <span>Download Report</span>
                    </Button>
                    {entry?.status === 'failed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRetry(entry)}
                        className="border-[#ff8c00] text-[#ff8c00] hover:bg-orange-50"
                      >
                        <Icon name="RotateCw" size={16} />
                        <span>Retry Upload</span>
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UploadHistory;